import React from 'react';
import { ImageFile } from '../types';
import { FileText, HardDrive, Camera, Compass, Grid, CheckCircle, AlertCircle, Loader2, HelpCircle } from 'lucide-react';

interface ValidationStep {
  id: string;
  name: string;
  icon: React.ComponentType<any>;
  status: 'pending' | 'running' | 'complete' | 'error';
  progress: number;
  issues?: number;
  description: string;
  tips?: string[];
}

interface Props {
  validationProgress: {
    step: string;
    progress: number;
    isValidating: boolean;
  };
  validationResults: any[];
  images: ImageFile[];
}

export const DetailedValidationSidebar: React.FC<Props> = ({ 
  validationProgress, 
  validationResults, 
  images 
}) => {
  // Track ACTUAL validation steps that we perform
  const getValidationSteps = (): ValidationStep[] => {
    const baseSteps: ValidationStep[] = [
      //adding a new first step - file consistency within a folder
      {
        id: 'file-type-consistency',
        name: 'File Type Consistency',
        icon: FileText,
        status: 'pending',
        progress: 0,
        description: 'Ensuring all files are the same type',
        tips: [
          'Upload only JPEG files OR only EXR files OR only PNG files', 
          'Mixed file types can cause reconstruction issues',
          'Convert all files to the same format before uploading'
        ]
      },
      {
        id: 'file-format',
        name: 'File Format',
        icon: FileText,
        status: 'pending',
        progress: 0,
        description: 'Checking supported image formats',
        tips: ['Use JPG, JPEG, or HEIC formats only', 'PNG and WebP are not supported by the reconstruction pipeline']
      },
      {
        id: 'file-format-consistency',
        name: 'File Format & Type Consistency',
        icon: FileText,
        status: 'pending',
        progress: 0,
        description: 'Checking supported formats and ensuring all files are the same type',
        tips: [
          'Supported: JPEG, HEIC, PNG, EXR formats only',
          'All files must be the same type (no mixing)',
          'EXR files are for HDR workflows only'
        ]
      },
      {
        id: 'file-size',
        name: 'File Size',
        icon: HardDrive,
        status: 'pending',
        progress: 0,
        description: 'Validating file sizes',
        tips: ['Keep images under 15MB each', 'Compress large images if needed']
      },
      {
        id: 'image-count',
        name: 'Image Count',
        icon: Camera,
        status: 'pending',
        progress: 0,
        description: 'Checking minimum images per camera',
        tips: ['Need at least 5 images per camera', 'More images = better reconstruction']
      },
      {
        id: 'landscape-mode',
        name: 'Landscape Orientation',
        icon: Compass,
        status: 'pending',
        progress: 0,
        description: 'Verifying landscape orientation',
        tips: ['Hold camera horizontally', 'Use orientation lock', 'All images must be landscape']
      },
      {
        id: 'resolution-consistency',
        name: 'Resolution Consistency',
        icon: Grid,
        status: 'pending',
        progress: 0,
        description: 'Checking resolution consistency per camera',
        tips: ['Use same camera settings', 'Avoid zoom changes', 'Keep resolution consistent']
      }
    ];

    // Update status based on current validation progress
    if (validationProgress.isValidating) {
      const currentStep = validationProgress.step.toLowerCase();
      
      baseSteps.forEach((step, index) => {
        if (currentStep.includes('file type consistency') && step.id === 'file-type-consistency') {
        step.status = 'running';
        step.progress = validationProgress.progress;
        } else if (currentStep.includes('format') && step.id === 'file-format') {
          step.status = 'running';
          step.progress = validationProgress.progress;
        } else if (currentStep.includes('size') && step.id === 'file-size') {
          step.status = 'running';
          step.progress = validationProgress.progress;
        } else if (currentStep.includes('count') && step.id === 'image-count') {
          step.status = 'running';
          step.progress = validationProgress.progress;
        } else if (currentStep.includes('orientation') && step.id === 'landscape-mode') {
          step.status = 'running';
          step.progress = validationProgress.progress;
        } else if (currentStep.includes('resolution') && step.id === 'resolution-consistency') {
          step.status = 'running';
          step.progress = validationProgress.progress;
        } else if (validationProgress.progress > (index * 20)) {
          step.status = 'complete';
          step.progress = 100;
        }
      });
    } else if (validationResults.length > 0) {
      // Update based on final results - map our validation rules to steps
      baseSteps.forEach(step => {
        const ruleMap: Record<string, string[]> = {
          'file-type-consistency': ['inconsistent-file-types'], //for all files in the folder to be of same type
          'file-format': ['invalid-format'],
          'file-size': ['file-too-large'],
          'image-count': ['no-images', 'insufficient-images'],
          'landscape-mode': ['portrait-orientation', 'invalid-metadata'],
          'resolution-consistency': ['resolution-inconsistent', 'corrupted-images']
        };
        
        const hasError = validationResults.some(result => 
          ruleMap[step.id]?.some(rule => result.rule.includes(rule))
        );
        
        step.status = hasError ? 'error' : 'complete';
        step.progress = 100;
        
        if (hasError) {
          const errorResults = validationResults.filter(result => 
            ruleMap[step.id]?.some(rule => result.rule.includes(rule))
          );
          step.issues = errorResults.reduce((total, result) => 
            total + (result.affectedImages?.length || 1), 0
          );
        }
      });
    } else {
      // If validation has run but no results, mark all as complete
      if (!validationProgress.isValidating && images.length > 0) {
        baseSteps.forEach(step => {
          step.status = 'complete';
          step.progress = 100;
        });
      }
    }

    return baseSteps;
  };

  const validationSteps = getValidationSteps();
  const completedSteps = validationSteps.filter(step => step.status === 'complete').length;
  const errorSteps = validationSteps.filter(step => step.status === 'error').length;

  const getProgressBarColor = (step: ValidationStep) => {
    if (step.status === 'error') return 'bg-[#0CB4E4]';
    if (step.status === 'complete') return 'bg-green-400';
    if (step.status === 'running') return 'bg-[#0C5484]';
    return 'bg-gray-700';
  };

  return (
    <div className="w-96 bg-[#161513] border-l border-[#0CB4E4]/20 overflow-y-auto">
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-white">Validation Progress</h3>
          {images.length > 0 && (
            <div className="text-sm text-gray-300">
              {images.length} images
            </div>
          )}
        </div>

        {/* Overall Progress */}
        {validationProgress.isValidating ? (
          <div className="mb-6 p-4 bg-[#0CB4E4]/10 border border-[#0CB4E4]/30 rounded-lg">
            <div className="flex items-center gap-3 mb-3">
              <Loader2 className="w-5 h-5 animate-spin text-[#0CB4E4]" />
              <span className="font-medium text-white">Validating Dataset</span>
            </div>
            <div className="text-sm text-[#0CB4E4] mb-2">{validationProgress.step}</div>
            <div className="w-full bg-[#161513] rounded-full h-2">
              <div 
                className="h-2 bg-[#0C5484] rounded-full transition-all duration-300"
                style={{ width: `${validationProgress.progress}%` }}
              />
            </div>
            <div className="text-xs text-[#0CB4E4] mt-1">{validationProgress.progress}% complete</div>
          </div>
        ) : validationResults.length > 0 ? (
          <div className={`mb-6 p-4 rounded-lg border ${
            errorSteps === 0 ? 'bg-green-900/20 border-green-400/30' : 'bg-[#0CB4E4]/10 border-[#0CB4E4]/30'
          }`}>
            <div className={`font-medium ${errorSteps === 0 ? 'text-green-400' : 'text-[#0CB4E4]'}`}>
              {errorSteps === 0 ? '✅ Dataset Ready' : `❌ ${errorSteps} Issue${errorSteps > 1 ? 's' : ''} Found`}
            </div>
            <div className={`text-sm mt-1 ${errorSteps === 0 ? 'text-green-400' : 'text-[#0CB4E4]'}`}>
              {completedSteps}/{validationSteps.length} checks passed
            </div>
          </div>
        ) : images.length === 0 ? (
          <div className="mb-6 p-4 bg-[#161513] border border-[#0CB4E4]/20 rounded-lg">
            <div className="text-gray-300">Upload images to start validation</div>
          </div>
        ) : null}

        {/* Detailed Validation Steps - ONLY REAL ONES */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-[#0CB4E4] uppercase tracking-wide">Validation Checks</h4>
          
          {validationSteps.map((step) => {
            const Icon = step.icon;
            return (
              <div key={step.id} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4 text-[#0CB4E4]" />
                    <span className="text-sm font-medium text-white">{step.name}</span>
                    <div className="group relative">
                      <HelpCircle className="w-3 h-3 text-gray-400 hover:text-gray-300" />
                      <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-64 p-3 bg-[#161513] text-white text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10 border border-[#0CB4E4]/30">
                        <div className="mb-2">{step.description}</div>
                        {step.tips && (
                          <div>
                            <div className="font-medium mb-1">Tips:</div>
                            {step.tips.map((tip, i) => (
                              <div key={i} className="text-gray-300">• {tip}</div>
                            ))}
                          </div>
                        )}
                        <div className="absolute left-1/2 -translate-x-1/2 top-full w-2 h-2 bg-[#161513] transform rotate-45 border-r border-b border-[#0CB4E4]/30" />
                      </div>
                    </div>
                  </div>
                  <span className={`text-sm font-medium ${
                    step.status === 'error' ? 'text-[#0CB4E4]' : 
                    step.status === 'complete' ? 'text-green-400' : 
                    'text-gray-400'
                  }`}>
                    {step.status === 'error' ? '✗' : 
                     step.status === 'complete' ? '✓' : 
                     step.status === 'running' ? `${step.progress}%` : '—'}
                  </span>
                </div>
                
                {step.status === 'running' && (
                  <div className="w-full bg-[#161513] rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-all duration-500 ${getProgressBarColor(step)}`}
                      style={{ width: `${step.progress}%` }}
                    />
                  </div>
                )}
                
                {step.status === 'error' && step.issues && (
                  <div className="text-xs text-[#0CB4E4]">
                    {step.issues} image{step.issues > 1 ? 's' : ''} affected
                  </div>
                )}
              </div>
            );
          })}

          {/* Info Box */}
          <div className="mt-6 p-4 bg-[#0CB4E4]/10 border border-[#0CB4E4]/30 rounded-lg">
            <div className="text-sm text-[#0CB4E4]">
              <strong>What we validate:</strong> File format, size, count, orientation, and resolution consistency. Advanced quality metrics coming soon!
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
